import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ICAL from 'ical.js';
import classNames from 'classnames';
import { FaCalendarPlus, FaExternalLinkAlt, FaInfoCircle } from 'react-icons/fa';
import { HiTrash } from 'react-icons/hi';
import { RootState } from '../../../redux/reducers';
import { Connection, ConnectionType } from '../../../redux/slices/connectionsSlice';
import ParticipantsPicker, { SelectOption } from '../misc/ParticipantsPicker';
import { ConnectionIdentifier } from '../../../types/misc';
import { EventType, NotificationSettings } from '../../../types/event';

import StepFlow, { StepFlowStep } from '../misc/StepFlow';
import calendarConnectionService from '../../../services/community/calendarConnectionService';
import eventService from '../../../services/community/eventService';
import IconBadge from '../icons/IconBadge';
import { eventTypeIcons } from '../../../utils/iconUtils';
import eventUtils from '../../../utils/eventUtils';
import ModalLayout from '../layouts/ModalLayout';
import Button from '../buttons/Button';
import EventNotificationForm from '../forms/EventNotificationForm';
import Slider from '../misc/Slider';

interface ImportEventsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

interface FileUploadViewProps {
  setStep: (step: 1 | -1) => void;
  selectedFile: File | null;
  // eslint-disable-next-line
  setSelectedFile: React.Dispatch<React.SetStateAction<File | null>>;
  icsEvents: any[];
  // eslint-disable-next-line
  setIcsEvents: React.Dispatch<React.SetStateAction<any[]>>;
}

interface ParticipantsViewProps {
  setStep: (step: 1 | -1) => void;
  selectedFile: File | null;
  resetForm: () => void;
  selectedEventType: EventType | undefined;
  setSelectedEventType: React.Dispatch<React.SetStateAction<EventType | undefined>>;
  notificationSettings: NotificationSettings;
  setNotificationSettings: React.Dispatch<React.SetStateAction<NotificationSettings>>;
  attendanceRequired: boolean;
  setAttendanceRequired: React.Dispatch<React.SetStateAction<boolean>>;
  setOpen: (open: boolean) => void;
}

export default function ImportEventsModal({
  open,
  setOpen,
}: ImportEventsModalProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState<number>(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [icsEvents, setIcsEvents] = useState<any[]>([]);
  const [selectedEventType, setSelectedEventType] = useState<EventType | undefined>(undefined);
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>(
    eventUtils.getMediumNotifications(false)
  );
  const [attendanceRequired, setAttendanceRequired] = useState<boolean>(true);

  const handleStep = (diff: 1 | -1) => setStep((prev) => prev + diff);

  const resetForm = () => {
    setStep(0);
    setSelectedFile(null);
    setIcsEvents([]);
    setSelectedEventType(undefined);
    setNotificationSettings(eventUtils.getMediumNotifications(false));
    setAttendanceRequired(true);
  };

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  const fileUploadProps: FileUploadViewProps = { setStep: handleStep, selectedFile, setSelectedFile, icsEvents, setIcsEvents };
  const participantsProps: ParticipantsViewProps = {
    setStep: handleStep,
    selectedFile,
    resetForm,
    selectedEventType,
    setSelectedEventType,
    notificationSettings,
    setNotificationSettings,
    attendanceRequired,
    setAttendanceRequired,
    setOpen
  };

  const steps = [
    <FileUploadView {...fileUploadProps} />,
    <ParticipantsView {...participantsProps} />,
  ];

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="max-w-4xl w-full mx-4 rounded-xl bg-secondary-50 p-6">
      <div className="flex items-center gap-3 mb-6">
        <FaCalendarPlus className="h-6 w-6 text-primary-500" />
        <h2 className="text-2xl font-semibold text-primary-900">
          {t('page.event.import.title')}
        </h2>
      </div>

      {/* External Calendar Promotion Banner */}
      <div className="mb-6 rounded-lg bg-primary-300 border p-4">
        <div className="flex items-start gap-3">
          <FaInfoCircle className="h-5 w-5 text-primary-900 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 className="text-sm font-medium text-primary-900 mb-1">
                  {t('page.event.import.automaticUpdates')}
                </h3>
                <p className="text-sm text-primary-900">
                  {t('page.event.import.linkExternalCalendar')}
                </p>
              </div>
              <Button
                variant="primary"
                onClick={() => {
                  setOpen(false);
                  navigate('/events', {
                    state: { tab: 'external-calendars' }
                  });
                }}
                className="flex items-center gap-2 whitespace-nowrap text-sm px-3 py-2"
              >
                <FaExternalLinkAlt className="h-4 w-4" />
                {t('page.event.import.linkCalendar')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-h-[70vh] overflow-y-auto">
        <StepFlow
          indexState={[step, setStep]}
          steps={steps}
          className='p-0'
        />
      </div>
    </ModalLayout>
  );
}

function FileUploadView({ setStep, selectedFile, setSelectedFile, icsEvents, setIcsEvents }: FileUploadViewProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const parseIcsFile = async (file?: File) => {
    setError(null);
    setIcsEvents([]);
    if (file && file.name.endsWith('.ics')) {
      setSelectedFile(file);
      const text = await file.text();
      try {
        const jcalData = ICAL.parse(text);
        const comp = new ICAL.Component(jcalData);
        const vevents = comp.getAllSubcomponents('vevent');
        const events = vevents.map((vevent) => {
          const event = new ICAL.Event(vevent);
          return {
            summary: event.summary,
            start: event.startDate ? event.startDate.toJSDate() : null,
            end: event.endDate ? event.endDate.toJSDate() : null,
            location: event.location,
            description: event.description,
          };
        });
        setIcsEvents(events);
      } catch (err) {
        setError(t('page.event.import.invalidFile'));
        setIcsEvents([]);
      }
    } else {
      setSelectedFile(null);
      setIcsEvents([]);
      setError(t('page.event.import.invalidFile'));
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    await parseIcsFile(file);
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setError(null);
    setIcsEvents([]);
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    await parseIcsFile(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  return (
    <StepFlowStep
      title={t('page.event.import.title')}
      subtitle={t('page.event.import.subtitle')}
      setStep={setStep}
      nextText={t('general.next')}
      showPrev={false}
      disabled={!selectedFile || error !== null}
      className='max-w-full'
    >
      <div className="flex flex-col gap-2">
        <div
          className="mb-1 flex h-fit w-full cursor-pointer flex-col rounded-lg border-2 border-dashed border-primary-900 p-4 hover:border-opacity-80 hover:bg-primary-300 hover:bg-opacity-10"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef?.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".ics"
            onChange={handleFileChange}
            className="hidden"
          />
          {!selectedFile && (
            <div className="mb-1 flex h-fit w-full flex-col items-center justify-center rounded-lg p-4">
              <FaCalendarPlus className="mb-1 h-14 w-14 text-primary-900" />
              <span className="items-center justify-center text-center text-sm font-medium text-primary-900">
                {t('page.event.import.uploadInstruction')}
              </span>
            </div>
          )}
          {selectedFile && (
            <div className="w-full flex flex-row items-center justify-between">
              <div className="text-green-700 text-sm text-center w-full">
                {t('page.event.import.selected')}: {selectedFile.name}
              </div>
              <button
                type="button"
                onClick={handleClearFile}
                className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-200 text-white focus:outline-none ml-2"
              >
                <HiTrash className="icon h-5 w-5" />
              </button>
            </div>
          )}
        </div>
        {icsEvents.length > 0 && (
          <div className="mt-4 max-h-80 overflow-y-auto rounded bg-secondary-200 p-3">
            <div className="mb-2 font-semibold text-primary-900">
              {t('page.event.import.previewEvents')}:
            </div>
            <ul className="text-sm">
              {icsEvents.map((ev: any, idx: number) => (
                <li key={idx} className="mb-2 border-b border-secondary-300 pb-1 last:border-0 last:pb-0">
                  <div className="font-medium text-primary-900">{ev.summary}</div>
                  <div className="text-xs text-primary-700">
                    {ev.start ? `${t('page.event.import.start')}: ${ev.start.toLocaleString()}` : ''}
                    {ev.end ? ` | ${t('page.event.import.end')}: ${ev.end.toLocaleString()}` : ''}
                  </div>
                  {ev.location && <div className="text-xs text-primary-700">{t('page.event.import.location')}: {ev.location}</div>}
                  {ev.description && <div className="text-xs text-primary-700">{ev.description}</div>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {error && (
          <div className="mt-2 text-red-600 text-sm">{error}</div>
        )}
      </div>
    </StepFlowStep>
  );
}

function ParticipantsView({
  setStep,
  selectedFile,
  resetForm,
  selectedEventType,
  setSelectedEventType,
  notificationSettings,
  setNotificationSettings,
  attendanceRequired,
  setAttendanceRequired,
  setOpen
}: ParticipantsViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<ConnectionIdentifier[]>([]);
  const [submitText, setSubmitText] = useState<string>(t('page.event.import.import'));
  const [disabled, setDisabled] = useState<boolean>(false);
  const [selectOption, setSelectOption] = useState<SelectOption>('active');

  const connections = useSelector((state: RootState) => state.connections);

  useEffect(() => {
    if (connections.length > 0 && selected.length === 0) {
      setSelected(connections);
    }
  }, [connections, selected.length]);

  const handleImport = async () => {
    if (!selectedEventType) {
      toast.error(t('page.event.import.eventTypeRequired'));
      return;
    }

    setDisabled(true);
    setSubmitText(t('page.event.import.importing'));

    let fileText = '';
    if (selectedFile) fileText = await selectedFile.text();
    else return;
    const toInvite = selected.filter(c => c.userId !== undefined).map(c => +(c.userId!));
    const toInviteExternal = selected.filter(c => c.externalMemberId !== undefined && c.externalMemberId !== null).map(c => ({ externalMemberId: c.externalMemberId! }));
    const inviteAll = selectOption === 'all';
    const inviteActive = selectOption === 'active';

    try {
      await calendarConnectionService.importEvent(
        fileText,
        toInvite,
        toInviteExternal,
        inviteAll,
        inviteActive,
        selectedEventType,
        notificationSettings,
        attendanceRequired
      );
      await eventService.getUpcomingEvents();
      toast.success(t('page.event.import.success'));
      resetForm();
      setOpen(false); // Close the modal
      navigate('/events');
    } catch (err) {
      console.error('Import failed', err);
      setDisabled(false);
      setSubmitText(t('page.event.import.import'));
    }
  };

  return (
    <StepFlowStep
      title={t('page.event.import.participants')}
      subtitle={t('page.event.import.participantsDescription')}
      setStep={setStep}
      showNext={false}
      onSubmit={handleImport}
      submitText={submitText}
      disabled={disabled || !selectedEventType}
      className='max-w-full'
      >
        {/* Event Type Selection */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-primary-900">
              {t('page.createEvent.type.title')}
            </label>
            <div className="flex flex-col gap-2">
              {Object.values(EventType).map((type) => (
                <div
                  key={type}
                  onClick={() => setSelectedEventType(type === selectedEventType ? undefined : type)}
                  className={classNames(
                    'flex w-full cursor-pointer justify-between gap-2 rounded-[8px] border border-secondary-200 px-4 py-2 transition-all',
                    { 'bg-secondary-200': selectedEventType === type },
                  )}>
                  <IconBadge icon={eventTypeIcons[type]} className="rounded-[6px] p-1" />
                  <div className="flex w-full items-center justify-between">
                    <h3 className="text-lg">
                      {t(`page.createEvent.type.type.${type.toLowerCase()}.title`)}
                    </h3>
                    <input type="checkbox" checked={selectedEventType === type} readOnly />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notification Settings */}
          <EventNotificationForm
            value={notificationSettings}
            onChange={setNotificationSettings}
            isDatePicker={false}
          />

          {/* Attendance Required Setting */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-primary-900">
              {t('page.createEvent.date.attendanceRequired')}
            </label>
            <Slider
              items={[
                { label: t('general.no'), value: false },
                { label: t('general.yes'), value: true },
              ]}
              value={attendanceRequired}
              onChange={setAttendanceRequired}
            />
          </div>
        </div>

        <ParticipantsPicker
          selected={selected}
          disable={(c) => !c.fields.EMAIL}
          setSelected={setSelected}
          selectOption={selectOption}
          setSelectOption={setSelectOption}
        />
    </StepFlowStep>
  );
}
